'use client'

import type { MouseEvent } from 'react'
import { useRouter } from '@/lib/next-shim'

import { useUpdateConsultationStatusMutation } from '@/core/graphql/hooks'
import { ConsultationStatus } from '@/core/graphql/types'

import type { Consultation } from './types'

/**
 * The two actions offered on a consultation card: cancel (status mutation) and
 * join (navigate to the waiting room when queued, otherwise straight to live).
 * Both stop propagation so the surrounding card's onClick doesn't also fire.
 */
export function useConsultationActions(consultation: Consultation) {
  const router = useRouter()
  const [updateConsultationStatus] = useUpdateConsultationStatusMutation()

  const isScheduled =
    consultation.status === ConsultationStatus.Scheduled ||
    consultation.status === ConsultationStatus.Accepted ||
    consultation.status === ConsultationStatus.Active
  const hasWaitingRoom = !!consultation.waitingRoom
  const canCancel = consultation.status === ConsultationStatus.Scheduled || consultation.status === ConsultationStatus.Accepted

  const cancel = (e: MouseEvent) => {
    e.stopPropagation()
    updateConsultationStatus({ variables: { payload: { consultationId: consultation._id, status: ConsultationStatus.Canceled } } })
  }

  const join = (e: MouseEvent) => {
    e.stopPropagation()
    if (hasWaitingRoom && isScheduled && consultation.status !== ConsultationStatus.Active) {
      router.push(`/account/consultations/${consultation._id}/waiting`)
    } else {
      router.push(`/account/consultations/${consultation._id}/live`)
    }
  }

  return { isScheduled, hasWaitingRoom, canCancel, cancel, join }
}
